/**
 * Audit Log Details Drawer (PR A9)
 * Shows detailed information about a single audit log entry
 * Displays all times in Eastern Time (ET)
 */
import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import './AuditLogDrawer.css';

const AuditLogDrawer = ({ log, isOpen, onClose }) => {
  if (!isOpen || !log) return null;

  // Format datetime in Eastern Time
  const formatDateET = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatInTimeZone(new Date(dateString), 'America/New_York', 'MMM d, yyyy h:mm:ss a zzz');
    } catch (error) {
      return dateString;
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`${label} copied to clipboard!`);
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  const prettifyJSON = (obj) => {
    if (!obj) return 'null';
    return JSON.stringify(obj, null, 2);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} />
      
      {/* Drawer */}
      <div className="audit-log-drawer">
        <div className="drawer-header">
          <h2>Audit Log Details</h2>
          <button className="drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="drawer-content">
          {/* Basic Info */}
          <section className="drawer-section">
            <h3>Event Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Log ID</label>
                <div className="info-value monospace">
                  {log.id}
                  <button 
                    className="btn-copy"
                    onClick={() => copyToClipboard(log.id, 'Log ID')}
                    title="Copy"
                  >
                    📋
                  </button>
                </div>
              </div>
              
              <div className="info-item">
                <label>Occurred At (ET)</label>
                <div className="info-value">{formatDateET(log.occurred_at)}</div>
              </div>
              
              <div className="info-item">
                <label>Resource</label>
                <div className="info-value">
                  <span className="resource-badge">{log.resource}</span>
                </div>
              </div>
              
              <div className="info-item">
                <label>Action</label>
                <div className="info-value">
                  <span className="action-badge">{log.action}</span>
                </div>
              </div>
              
              {log.result && (
                <div className="info-item">
                  <label>Result</label>
                  <div className="info-value">
                    <span className={`result-badge result-${log.result.toLowerCase()}`}>
                      {log.result === 'success' ? '✓' : '✗'} {log.result}
                    </span>
                  </div>
                </div>
              )}
              
              {log.entity_id && (
                <div className="info-item">
                  <label>Entity ID</label>
                  <div className="info-value monospace">{log.entity_id}</div>
                </div>
              )}
            </div>
          </section>

          {/* Actor Info */}
          <section className="drawer-section">
            <h3>Actor</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Actor ID</label>
                <div className="info-value monospace">{log.actor_id || 'N/A'}</div>
              </div>
              
              {log.actor_name && (
                <div className="info-item">
                  <label>Actor Name</label>
                  <div className="info-value">{log.actor_name}</div>
                </div>
              )}
              
              {log.impersonator_id && (
                <>
                  <div className="info-item">
                    <label>Impersonator ID</label>
                    <div className="info-value monospace">
                      <span className="impersonation-badge">
                        🎭 {log.impersonator_id}
                      </span>
                    </div>
                  </div>
                  
                  {log.impersonator_name && (
                    <div className="info-item">
                      <label>Impersonator Name</label>
                      <div className="info-value">{log.impersonator_name}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Request Info */}
          {(log.ip_address || log.user_agent || log.request_id) && (
            <section className="drawer-section">
              <h3>Request Details</h3>
              <div className="info-grid">
                {log.ip_address && (
                  <div className="info-item">
                    <label>IP Address</label>
                    <div className="info-value monospace">{log.ip_address}</div>
                  </div>
                )}
                
                {log.request_id && (
                  <div className="info-item">
                    <label>Request ID</label>
                    <div className="info-value monospace">{log.request_id}</div>
                  </div>
                )}
                
                {log.user_agent && (
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <label>User Agent</label>
                    <div className="info-value user-agent">{log.user_agent}</div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Before/After State */}
          {(log.before || log.after) && (
            <section className="drawer-section">
              <h3>State Changes</h3>
              
              {log.before && (
                <div className="json-container">
                  <div className="json-header">
                    <label>Before</label>
                    <button 
                      className="btn-copy"
                      onClick={() => copyToClipboard(prettifyJSON(log.before), 'Before state')}
                    >
                      📋 Copy JSON
                    </button>
                  </div>
                  <pre className="json-content">{prettifyJSON(log.before)}</pre>
                </div>
              )}
              
              {log.after && (
                <div className="json-container">
                  <div className="json-header">
                    <label>After</label>
                    <button 
                      className="btn-copy"
                      onClick={() => copyToClipboard(prettifyJSON(log.after), 'After state')}
                    >
                      📋 Copy JSON
                    </button>
                  </div>
                  <pre className="json-content">{prettifyJSON(log.after)}</pre>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default AuditLogDrawer;
